import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Users, Activity, Plus, CheckCircle, Copy, Info, Check, Link as LinkIcon, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { b2bAPI, paymentAPI } from '../utils/api';
import authService from '../firebase/authService';

const B2BAdminDashboardPage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [isCheckingTier, setIsCheckingTier] = useState(true);

    useEffect(() => {
        return authService.onAuthStateChange(setUser);
    }, []);

    useEffect(() => {
        if (!user) return;
        const checkTier = async () => {
            try {
                const subRes = await paymentAPI.getUserSubscription();
                if (subRes?.subscription?.tier !== 'enterprise') {
                    toast.error("Enterprise Plan required to access the B2B portal.");
                    navigate('/dashboard');
                }
            } catch (err) {
                console.error("Error checking subscription:", err);
                navigate('/dashboard');
            } finally {
                setIsCheckingTier(false);
            }
        };
        checkTier();
    }, [user, navigate]);

    const [orgId, setOrgId] = useState(localStorage.getItem('b2b_org_id') || '');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    // Form states
    const [newOrgName, setNewOrgName] = useState('');
    const [newOrgDomain, setNewOrgDomain] = useState('');

    const [newCohortName, setNewCohortName] = useState('');
    const [newCohortCode, setNewCohortCode] = useState('');

    const fetchStats = async (id) => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await b2bAPI.getOrgStats(id);
            if (res.success) {
                setStats(res);
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to load organization stats.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (orgId) {
            fetchStats(orgId);
        }
    }, [orgId]);

    const handleCreateOrg = async (e) => {
        e.preventDefault();
        try {
            const res = await b2bAPI.createOrganization({ name: newOrgName, domain: newOrgDomain });
            if (res.success) {
                toast.success(`Organization ${newOrgName} created!`);
                setOrgId(res.org_id);
                localStorage.setItem('b2b_org_id', res.org_id);
                setNewOrgName('');
                setNewOrgDomain('');
            }
        } catch (err) {
            toast.error('Failed to create organization');
        }
    };

    const handleCreateCohort = async (e) => {
        e.preventDefault();
        if (!orgId) return toast.error('Must have an organization first');
        try {
            const res = await b2bAPI.createCohort({
                org_id: orgId,
                cohort_name: newCohortName,
                invite_code: newCohortCode.toUpperCase()
            });
            if (res.success) {
                toast.success(`Cohort ${newCohortName} created!`);
                setNewCohortName('');
                setNewCohortCode('');
                fetchStats(orgId);
            }
        } catch (err) {
            toast.error('Failed to create cohort or invite code is taken');
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        toast.success('Invite code copied to clipboard!');
    };

    if (isCheckingTier) {
        return (
            <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0B0F19] p-8 text-gray-100 flex flex-col md:flex-row gap-8 font-sans">

            {/* Sidebar Setup */}
            <div className="w-full md:w-1/3 space-y-6">
                <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                    <h2 className="text-xl font-bold mb-4 flex items-center text-cyan-400">
                        <Building className="w-5 h-5 mr-2" /> Setup Organization
                    </h2>
                    {!orgId ? (
                        <form onSubmit={handleCreateOrg} className="space-y-4">
                            <div>
                                <label className="text-sm text-gray-400 mb-1 block">Organization Name</label>
                                <input required value={newOrgName} onChange={e => setNewOrgName(e.target.value)} className="w-full bg-gray-800 border border-gray-700 rounded p-2 focus:border-cyan-500 outline-none" placeholder="e.g. Stanford University" />
                            </div>
                            <div>
                                <label className="text-sm text-gray-400 mb-1 block">Domain</label>
                                <input required value={newOrgDomain} onChange={e => setNewOrgDomain(e.target.value)} className="w-full bg-gray-800 border border-gray-700 rounded p-2 focus:border-cyan-500 outline-none" placeholder="e.g. stanford.edu" />
                            </div>
                            <button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2 rounded transition">Create Organization</button>
                        </form>
                    ) : (
                        <div className="text-center p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-xl">
                            <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                            <p className="text-emerald-300 font-medium">Organization Active</p>
                            <p className="text-xs text-gray-400 font-mono mt-1">ID: {orgId}</p>
                            <button onClick={() => { setOrgId(''); localStorage.removeItem('b2b_org_id'); setStats(null); }} className="text-xs text-slate-500 underline mt-4 hover:text-white">Reset Demo</button>
                        </div>
                    )}
                </div>

                {orgId && (
                    <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                        <h2 className="text-xl font-bold mb-4 flex items-center text-indigo-400">
                            <Plus className="w-5 h-5 mr-2" /> Add Cohort
                        </h2>
                        <form onSubmit={handleCreateCohort} className="space-y-4">
                            <div>
                                <label className="text-sm text-gray-400 mb-1 block">Cohort Name</label>
                                <input required value={newCohortName} onChange={e => setNewCohortName(e.target.value)} className="w-full bg-gray-800 border border-gray-700 rounded p-2 focus:border-indigo-500 outline-none" placeholder="e.g. CS Class of 2026" />
                            </div>
                            <div>
                                <label className="text-sm text-gray-400 mb-1 block">Unique Invite Code</label>
                                <input required value={newCohortCode} onChange={e => setNewCohortCode(e.target.value.toUpperCase())} className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-indigo-300 font-mono focus:border-indigo-500 outline-none" placeholder="STAN-CS-26" />
                            </div>
                            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 rounded transition">Create Cohort</button>
                        </form>
                    </div>
                )}
            </div>

            {/* Main Dashboard Area */}
            <div className="w-full md:w-2/3">
                <div className="flex items-center justify-between mb-2">
                    <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400">Enterprise Portal</h1>
                    {stats && <div className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-full text-xs font-mono font-bold flex items-center"><Shield className="w-3 h-3 mr-1" /> ACTIVE</div>}
                </div>
                <p className="text-gray-400 mb-8 text-lg">Manage your institution, track student onboarding, and measure cohort skill progression.</p>

                {loading ? (
                    <div className="flex items-center justify-center p-20"><div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div></div>
                ) : stats ? (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bg-gray-900 border border-gray-800 p-6 rounded-2xl flex flex-col items-center justify-center text-center">
                                <Building className="w-8 h-8 text-cyan-500 mb-2" />
                                <h3 className="text-3xl font-bold text-white mb-1">{stats.org_name}</h3>
                                <p className="text-xs text-gray-400 uppercase tracking-widest">Active Org</p>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 p-6 rounded-2xl flex flex-col items-center justify-center text-center">
                                <Users className="w-8 h-8 text-indigo-500 mb-2" />
                                <h3 className="text-3xl font-bold text-white mb-1">{stats.total_students}</h3>
                                <p className="text-xs text-gray-400 uppercase tracking-widest">Global Students</p>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 p-6 rounded-2xl flex flex-col items-center justify-center text-center">
                                <Activity className="w-8 h-8 text-emerald-500 mb-2" />
                                <h3 className="text-3xl font-bold text-white mb-1">{stats.cohorts?.length || 0}</h3>
                                <p className="text-xs text-gray-400 uppercase tracking-widest">Active Cohorts</p>
                            </div>
                        </div>

                        <h2 className="text-xl font-semibold mt-8 mb-4">Your Cohorts</h2>
                        {stats.cohorts && stats.cohorts.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {stats.cohorts.map((cohort, idx) => (
                                    <div key={idx} className="bg-gray-900 border border-gray-800 p-5 rounded-xl hover:border-gray-700 transition">
                                        <div className="flex justify-between items-start mb-4">
                                            <h4 className="font-bold text-lg text-indigo-100">{cohort.name}</h4>
                                            <div className="bg-indigo-500/10 text-indigo-400 px-2 py-1 rounded-md text-xs font-mono flex items-center cursor-pointer hover:bg-indigo-500/20" onClick={() => copyCode(cohort.inviteCode)}>
                                                {cohort.inviteCode} <Copy className="w-3 h-3 ml-2" />
                                            </div>
                                        </div>
                                        <div className="flex items-center text-gray-400 mt-4 text-sm">
                                            <Users className="w-4 h-4 mr-2" /> {cohort.studentCount} Students Onboarded
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-12 border border-dashed border-indigo-500/30 bg-indigo-500/5 rounded-2xl flex flex-col items-center justify-center text-center">
                                <div className="w-16 h-16 bg-indigo-500/10 rounded-full flex items-center justify-center mb-4">
                                    <Plus className="w-8 h-8 text-indigo-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">Ready to onboard students?</h3>
                                <p className="text-gray-400 max-w-md mb-6">Create your first Cohort from the sidebar. You'll get a unique Invite Code to share with your class or team seamlessly granting them Enterprise access.</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div className="p-10 border-2 border-dashed border-slate-700 bg-slate-900 rounded-3xl flex flex-col items-center justify-center text-center">
                            <Building className="w-16 h-16 text-slate-500 mb-6" />
                            <h2 className="text-2xl font-bold text-white mb-4">Welcome to Rastor AI Enterprise</h2>
                            <p className="text-slate-400 text-lg max-w-lg mb-8">Set up your organization to start importing students, tracking their skills, and managing premium accesses in bulk.</p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left w-full mt-4">
                                <div className="bg-slate-800 p-5 rounded-2xl">
                                    <div className="w-10 h-10 bg-cyan-500/20 text-cyan-400 rounded-full flex items-center justify-center font-bold mb-3">1</div>
                                    <h4 className="font-bold text-white mb-1">Create Organization</h4>
                                    <p className="text-sm text-slate-400">Register your University or Company in the sidebar to activate the dashboard.</p>
                                </div>
                                <div className="bg-slate-800 p-5 rounded-2xl">
                                    <div className="w-10 h-10 bg-indigo-500/20 text-indigo-400 rounded-full flex items-center justify-center font-bold mb-3">2</div>
                                    <h4 className="font-bold text-white mb-1">Add Cohorts</h4>
                                    <p className="text-sm text-slate-400">Group students by graduating class or department. Get a unique Invite Code.</p>
                                </div>
                                <div className="bg-slate-800 p-5 rounded-2xl">
                                    <div className="w-10 h-10 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center font-bold mb-3">3</div>
                                    <h4 className="font-bold text-white mb-1">Students Join</h4>
                                    <p className="text-sm text-slate-400">Students enter the code in their Profile settings to automatically join your org.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default B2BAdminDashboardPage;
